import { Button, CrashIcon, EmptyState } from '@lmc/ui-kit'
import * as Sentry from '@sentry/react'
import type React from 'react'
import styled from 'styled-components'

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const isStaleChunkError = (error: unknown) => {
  return (
    error instanceof Error &&
    (error.message.includes('Loading chunk') ||
      error.message.includes('dynamically imported module') ||
      error.message.includes('Content-Type'))
  )
}

type ErrorFallbackProps = {
  error: Error
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const refreshPage = () => {
    window.location.reload()
  }

  if (isStaleChunkError(error)) {
    return (
      <ErrorContainer>
        <EmptyState
          title="The Care Office was updated"
          description="Please refresh the page to get the latest version"
          icon={<CrashIcon />}
        />
        <Button variant="primary" onClick={refreshPage}>
          Refresh now
        </Button>
      </ErrorContainer>
    )
  }

  return (
    <ErrorContainer>
      <EmptyState
        title="Whoops! There was a crash..."
        description="Don't worry, try refreshing the page and if the problem persists contact support."
        icon={<CrashIcon />}
      />
      <Button variant="primary" onClick={refreshPage}>
        Refresh page
      </Button>
    </ErrorContainer>
  )
}

const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <ErrorFallback error={error} resetError={resetError} />
    )}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
)

export default ErrorBoundary
